import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { fieldType } from 'src/app/shared/const/options';
import { orderTags } from 'src/app/shared/const/orders';
import { priceTags } from 'src/app/shared/const/prices.const';
import { DateChecker, getDaysBetweenDatesByBusinessDay } from '@shared-logic/utils';
import { wayPayNames } from '@shared-logic/payments';
import { appState } from 'src/app/shared/interfaces/appState.interface';
import { PriceValidator } from 'src/app/shared/price';
import { WAY_PAY_VOID, WayPayValidator, isWayPayExpirationRelevant, isWayPayExpirationRequired } from '@shared-logic/payments';
import { Price, initialPrice, typeCoins } from '@shared-logic/prices';
import { Order, getBuyerExchange } from '@shared-logic/orders';
import { Field, isPriceOrExchange } from '../fields.component';

@Component({
  selector: 'app-expiration',
  templateUrl: './expiration.component.html',
  styleUrls: ['./../fields.component.scss']
})
export class ExpirationComponent extends Field implements OnInit, OnDestroy {
  @Input() isOperator: boolean = false;
  isFechaFija: boolean = false;
  isFixExpiration: boolean = false;
  isContraEntrega: boolean = true;

  wayPaySubscription!: Subscription;
  private exchange$!: Subscription;
  private exchangeMother: Order | null = null; // Mother order of the exchange being edited
  constructor(store: Store<appState>, private date: DateChecker, private _adapter: DateAdapter<any>,
    private priceV: PriceValidator, private wayPayV: WayPayValidator) {
    super(store);
    this._adapter.setLocale('es');
    this.exchange$ = this.store.select('exchange', 'generateOrder').subscribe(generateOrder => {
      this.exchangeMother = generateOrder;
    });
    this.elementSelected = '';
    this.wayPaySubscription = this.store.subscribe(state => {
      setTimeout(() => {
        const price = this.getPrice();
        if (price) {
          this.isContraEntrega = this.wayPayV.getIsContraEntrega(price);
          this.isFechaFija = this.wayPayV.getIsFechaFija(price);
          this.isFixExpiration = this.priceV.isFixExpiration(price);
        }
      }, 100)

    })
  }

  ngOnInit(): void {
    this.setTag(priceTags.wayPayExpiration);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.wayPaySubscription?.unsubscribe();
    this.exchange$?.unsubscribe();
  }

  /**
   * The exchange has no Price of its own: it uses the mother price (or the empty price before the mother is loaded)
   * with the exchange currency and payment method.
   */
  override getPrice(): Price {
    if (this.fieldIdentificator == fieldType.EXCHANGE) {
      const exchange = this.elementToGenerate;
      const basePrice: Price = this.exchangeMother?.price ?? initialPrice;
      return { ...basePrice, typeCoin: exchange?.typeCoin ?? '--', wayPay: exchange?.wayPay ?? WAY_PAY_VOID };
    }
    return super.getPrice();
  }

  private getExchangeBuyer(): number {
    return this.exchangeMother ? getBuyerExchange(this.exchangeMother) : 0;
  }

  /** The exchange shows the field only when the expiration changes its IBM code. */
  isVisible(): boolean {
    if (this.fieldIdentificator != fieldType.EXCHANGE) {
      return true;
    }
    return isWayPayExpirationRelevant(this.elementToGenerate?.wayPay, this.elementToGenerate?.typeCoin ?? '', this.getExchangeBuyer());
  }


  override getIsFieldVoid(): boolean {
    // For the exchange, only a required expiration counts as missing (it is optional for letters of guarantee in USD).
    if (this.fieldIdentificator == fieldType.EXCHANGE) {
      return this.isNecesaryExpiration() && this.elementSelected == '';
    }
    return !this.isContraEntrega && this.elementSelected == ''
  }

  override rememberValueInForm() {
    if (this.subTag == priceTags.wayPayExpiration || this.thirdTag == priceTags.wayPayExpiration) {
      this.rememberValueTypeThree();
    }
  }

  override setElement(element: any, index: number): void {
    this.setTag(priceTags.wayPayExpiration);
    this.setElementToGenerate(element);
    this.verifyDaysToActualizate();
  }

  isNecesaryExpiration() {
    if (this.fieldIdentificator == fieldType.EXCHANGE) {
      return isWayPayExpirationRequired(this.elementToGenerate?.wayPay, this.elementToGenerate?.typeCoin ?? '', this.getExchangeBuyer());
    }
    return !this.isContraEntrega && !this.wayPayV.getIsConCartaDeGarantia(this.getPrice()) &&
      !(this.getPrice() ? this.getPrice().typeCoin : '' == typeCoins.USD);
  }

  setOption() {
    this.setTag(priceTags.wayPayOptions);
    this.setElementToGenerate(this.isFixExpiration ? 0 : 1);

    this.setTag(priceTags.wayPayExpiration);
  }

  override setTag(nameOfTag: string): void {
    if (isPriceOrExchange(this.fieldIdentificator)) {
      this.tag = priceTags.wayPay;
      this.subTag = nameOfTag;
      this.thirdTag = '';
    } else {
      this.tag = orderTags.price;
      this.subTag = priceTags.wayPay;
      this.thirdTag = nameOfTag;
    }
  }

  verifyDaysToActualizate() {
    const price = this.getPrice();
    let wayPay = price.wayPay;
    let typeCoin = price.typeCoin;
    let expiration = wayPay.expiration;
    let typeBusiness = price.typeBusiness;
    let isBusinessDay = wayPay.isBusinessDays == 1;

    let newDays = 0;
    if (wayPay.wayPayName == wayPayNames.conCartaGarantia) {
      if (typeCoin == typeCoins.ARS || (typeCoin == typeCoins.USD && wayPay.percentage != 97.5)) {
        newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration, isBusinessDay);
      }
    } else if (wayPay.wayPayName == wayPayNames.fechaFija) {
      if (typeBusiness == 1 || typeBusiness == 3) {
        newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration, isBusinessDay);
        //Por default 5 días hábiles o corridos del día posterior o sino se coloca
        //el vencimiento manual - la mercadería tiene que estar entregada para cobrar"
      } else if (typeBusiness == 2) {
        newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration, isBusinessDay);
      } else if (typeBusiness == 4) {
        //Último día hábil del mes de entrega
        //¿Calculamos los días? Si.
        newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration, isBusinessDay);
      }
    } else if (wayPay.wayPayName == wayPayNames.diferidoPesificacion) {
      //newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration,isBusinessDay);
    } else if (wayPay.wayPayName == wayPayNames.diferidoFacturacion) {
      //newDays = getDaysBetweenDatesByBusinessDay(this.date.getDate(), expiration,isBusinessDay);
    }

    if (newDays > 0) {
      this.setTag(priceTags.wayPayDays);
      this.setElementToGenerate(newDays);
      this.setTag(priceTags.wayPayExpiration);
    }

  }

}
